import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useMutation, useQuery } from '@apollo/client';
import dayjs from 'dayjs';
import { CREATE_DEVICE_COMMAND, GET_DEVICE_COMMANDS } from './queries';
import FeedFloDateRangeInput from '../FeedFloDateRangeInput/FeedFloDateRangeInput';
import { DATE_TIME_FORMAT_MONTH_DAY_HOUR_MINUTE_SECOND } from '../../utils/dates';
import { Refresh } from '../../atoms/Icons';
import './DeviceCard.scss';

export default function DeviceCommandsSection({ deviceId, deviceType }) {
  // --- State hooks ---
  const [loadingCommand, setLoadingCommand] = useState(null);
  const [commandError, setCommandError] = useState(null);
  const [commandSuccess, setCommandSuccess] = useState(null);
  const [restartAt, setRestartAt] = useState('');
  const [debugBits, setDebugBits] = useState('');
  const [lensDirty, setLensDirty] = useState('true');
  const [activeBinIndex, setActiveBinIndex] = useState('0');
  const now = useMemo(() => dayjs.tz(), []);
  const [dateRange, setDateRange] = useState({
    from: now.subtract(7, 'day').startOf('day'),
    to: now.endOf('day'),
  });

  // --- Apollo/GraphQL hooks ---
  const {
    data: commandsData,
    loading: commandsLoading,
    refetch,
  } = useQuery(GET_DEVICE_COMMANDS, {
    variables: {
      deviceId,
      from: dateRange.from.unix(),
      to: dateRange.to.unix(),
    },
    context: {
      headers: {
        'x-hasura-role': 'user',
      },
    },
    skip: !deviceId,
  });

  const [createCommand] = useMutation(CREATE_DEVICE_COMMAND, {
    context: {
      headers: {
        'x-hasura-role': 'user',
      },
    },
    onCompleted: (data) => {
      setLoadingCommand(null);
      const commandType = data?.insert_device_command_one?.type;
      setCommandSuccess(`Command "${commandType}" created successfully!`);
      setTimeout(() => setCommandSuccess(null), 3000);
      // Refetch commands to show the new command
      if (refetch) {
        refetch();
      }
    },
    onError: (error) => {
      console.error('Failed to create device command:', error);
      setLoadingCommand(null);
      setCommandError('Failed to create command. Please try again.');
      setTimeout(() => setCommandError(null), 5000);
    },
  });

  // --- Function definitions ---
  const handleCreateCommand = async (type, data) => {
    if (!deviceId || loadingCommand) {
      return;
    }

    setLoadingCommand(type);
    setCommandError(null);
    setCommandSuccess(null);

    try {
      await createCommand({
        variables: {
          deviceId,
          type,
          data,
        },
      });
    } catch (error) {
      // Error handled in onError callback
    }
  };

  const handleDateRangeChange = (event) => {
    if (event?.dateRange) {
      setDateRange(event.dateRange);
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '—';
    return dayjs.tz(1000 * timestamp).format(DATE_TIME_FORMAT_MONTH_DAY_HOUR_MINUTE_SECOND);
  };

  const formatData = (data) => {
    if (!data) return '—';
    try {
      return JSON.stringify(data, null, 2);
    } catch {
      return String(data);
    }
  };

  const handleRestart = () => {
    if (!restartAt) {
      setCommandError('Select a restart date and time.');
      setTimeout(() => setCommandError(null), 5000);
      return;
    }
    const time = Math.floor(new Date(restartAt).getTime() / 1000);
    if (!Number.isFinite(time) || time <= 0) {
      setCommandError('Invalid restart date and time.');
      setTimeout(() => setCommandError(null), 5000);
      return;
    }
    handleCreateCommand('Restart', { time });
  };

  const handleDebug = () => {
    const normalizedBits = debugBits.replace(/[^0-9a-fA-F]/g, '');
    if (!normalizedBits || normalizedBits.length % 2 !== 0) {
      setCommandError('Enter an even-length hex string (e.g. 0A3F).');
      setTimeout(() => setCommandError(null), 5000);
      return;
    }
    handleCreateCommand('Debug', { bits: normalizedBits });
  };

  const handleSetDirty = () => {
    handleCreateCommand('Set Lens Dirty', { dirty: lensDirty === 'true' });
  };

  const handleSetActiveBin = () => {
    handleCreateCommand('Set Active Bin', { bin_index: Number(activeBinIndex) });
  };

  const isButtonDisabled = (commandType) => {
    return loadingCommand !== null || !deviceId;
  };

  // --- Render ---
  return (
    <div className="DeviceCard-commandsSection">
      {commandError && <div className="DeviceCard-commandError">{commandError}</div>}
      {commandSuccess && <div className="DeviceCard-commandSuccess">{commandSuccess}</div>}
      <div className="DeviceCard-commandList">
        <div className="DeviceCard-commandRow">
          <button
            type="button"
            className="DeviceCard-commandButton"
            onClick={handleRestart}
            disabled={isButtonDisabled('Restart')}
          >
            {loadingCommand === 'Restart' ? 'Creating...' : 'Restart'}
          </button>
          <div className="DeviceCard-commandOptions">
            <input
              id={`restart-at-${deviceId}`}
              type="datetime-local"
              className="DeviceCard-commandOptions-input"
              aria-label="Restart at"
              value={restartAt}
              onChange={(event) => setRestartAt(event.target.value)}
              disabled={isButtonDisabled('Restart')}
            />
          </div>
        </div>

        <hr className="DeviceCard-commandDivider" />

        <div className="DeviceCard-commandRow">
          <button
            type="button"
            className="DeviceCard-commandButton"
            onClick={handleDebug}
            disabled={isButtonDisabled('Debug')}
          >
            {loadingCommand === 'Debug' ? 'Creating...' : 'Debug'}
          </button>
          <div className="DeviceCard-commandOptions">
            <input
              id={`debug-bits-${deviceId}`}
              type="text"
              className="DeviceCard-commandOptions-input"
              aria-label="Debug bits (hex)"
              value={debugBits}
              onChange={(event) => setDebugBits(event.target.value)}
              placeholder="Hex bits (e.g. 0A3F)"
              disabled={isButtonDisabled('Debug')}
            />
          </div>
        </div>

        {/* Set dirty - only for FeedLine */}
        {deviceType === 'FeedLine' && (
          <>
            <hr className="DeviceCard-commandDivider" />
            <div className="DeviceCard-commandRow">
              <button
                type="button"
                className="DeviceCard-commandButton"
                onClick={handleSetDirty}
                disabled={isButtonDisabled('Set Lens Dirty')}
              >
                {loadingCommand === 'Set Lens Dirty' ? 'Creating...' : 'Set Dirty'}
              </button>
              <div className="DeviceCard-commandOptions">
                <select
                  id={`lens-dirty-${deviceId}`}
                  className="DeviceCard-commandOptions-input"
                  aria-label="Lens dirty value"
                  value={lensDirty}
                  onChange={(event) => setLensDirty(event.target.value)}
                  disabled={isButtonDisabled('Set Lens Dirty')}
                >
                  <option value="true">True</option>
                  <option value="false">False</option>
                </select>
              </div>
            </div>
          </>
        )}

        {/* Set active bin - only for BinController */}
        {deviceType === 'BinController' && (
          <>
            <hr className="DeviceCard-commandDivider" />
            <div className="DeviceCard-commandRow">
              <button
                type="button"
                className="DeviceCard-commandButton"
                onClick={handleSetActiveBin}
                disabled={isButtonDisabled('Set Active Bin')}
              >
                {loadingCommand === 'Set Active Bin' ? 'Creating...' : 'Set Bin'}
              </button>
              <div className="DeviceCard-commandOptions">
                <select
                  id={`active-bin-${deviceId}`}
                  className="DeviceCard-commandOptions-input"
                  aria-label="Active bin index"
                  value={activeBinIndex}
                  onChange={(event) => setActiveBinIndex(event.target.value)}
                  disabled={isButtonDisabled('Set Active Bin')}
                >
                  <option value="0">0</option>
                  <option value="1">1</option>
                </select>
              </div>
            </div>
          </>
        )}
      </div>

      <div className="DeviceCard-commandsTableSection">
        <div className="DeviceCard-commandsTableHeader">
          <FeedFloDateRangeInput from={dateRange.from} to={dateRange.to} max={now} onChange={handleDateRangeChange} />
          <button
            type="button"
            className="DeviceCard-refreshButton"
            onClick={() => refetch()}
            disabled={commandsLoading}
            title="Refresh commands"
          >
            <Refresh className="DeviceCard-refreshIcon" width="1.6rem" height="1.6rem" />
          </button>
        </div>
        <div className="DeviceCard-commandsTableHolder">
          {commandsLoading ? (
            <div>Loading commands...</div>
          ) : (
            <table className="DeviceCard-commandsTable">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Created At</th>
                  <th>Sent At</th>
                  <th>Ack At</th>
                  <th>Data</th>
                </tr>
              </thead>
              <tbody>
                {commandsData?.device_command?.length > 0 ? (
                  commandsData.device_command.map((command) => (
                    <tr key={command.id}>
                      <td>{command.type || '—'}</td>
                      <td>{formatTimestamp(command.created_at)}</td>
                      <td>{formatTimestamp(command.sent_at)}</td>
                      <td>{formatTimestamp(command.acknowledged_at)}</td>
                      <td>
                        <pre className="DeviceCard-commandData">{formatData(command.data)}</pre>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" style={{ textAlign: 'center', padding: '1.6rem' }}>
                      No commands found for the selected date range
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

DeviceCommandsSection.propTypes = {
  deviceId: PropTypes.string.isRequired,
  deviceType: PropTypes.string,
};
